import React from 'react';
import SearchIcon from '@material-ui/icons/Search';
import TimelineIcon from '@material-ui/icons/Timeline';
import {
    TableFooter,
    TableCell,
    TableRow,
    TextField,
    makeStyles,
    Typography,
    TextareaAutosize,
    FormControlLabel,
    Checkbox,
} from '@material-ui/core';
import { tableStyle } from '../../UIComponent/CommonStyle';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { createTheme, ThemeProvider } from '@mui/material';
import { isDesktop, isTablet } from "react-device-detect";
import { checkIsTabletDevice } from '../../Function/globalFunction';

const useStyles = makeStyles((theme) => ({
    ...tableStyle(theme),
    footerCell: {
        borderBottom: 'none',
        color: '#232F3F',
        fontSize: 14,
        fontWeight: 'bolder',
    },
    totalHours: {
        textAlign: 'end',
    },
    descriptionCell: {
        wordWrap: 'break-word',
        borderBottom: 'none',
        padding: 0,
        whiteSpace: 'pre-line',
        minWidth: (checkIsTabletDevice() || isTablet) ? '90%' : isDesktop ? 520 : 'auto',
    },
    fontSize: {
        fontSize: '12.25px',
    },
    dateWidth: {
        minWidth: isDesktop ? 115 : 'auto',
    },
    expiryDate: {
        minWidth: isDesktop ? 255 : 'auto',
        display: 'flex',
    },
    expiryText: {
        color: 'red',
        fontWeight: 'bold',
    },
    updatedText: {
        display: 'flex',
        alignItems: 'center',
        color: 'green',
        fontWeight: 'bold',
        marginLeft: 15,
        background: 'white',
        borderRadius: 20,
        padding: 5,
    },
}));

const theme = createTheme({
    components: {
        MuiDateCalendar: {
            styleOverrides: {
                root: {
                    height: '288px',
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                root: {
                    zIndex: 999,
                }
            }
        },
    }
});

export function getColumnsForBalanceRecord(
    balanceData,
    intl,
    page,
    handleInputChange,
    updatedIds,
    staffDuty
) {
    const classes = useStyles();
    const isShiftDuty = localStorage.getItem('staff_duty') === window.global.shiftDuty || staffDuty === window.global.shiftDuty;
    const isFMDepartment = localStorage.getItem('department_id') == 3;
    const hasCashOut = balanceData.some(item => {
        const cashOutValue = parseFloat(item.cashOut);
        return !isNaN(cashOutValue) && cashOutValue > 0;
    });
    const isEmployeeRecordPage = page == 'Employee Record';
    const showCashOut = isShiftDuty || hasCashOut;

    let columns = [
        {
            name: 'id',
            label: 'ID',
            options: {
                filter: false,
                sort: false,
                display: false,
            },
        },
        {
            name: 'dataSubmitted',
            label: intl.formatMessage({ id: 'comp_leave.duty_date' }),
            options: {
                filter: true,
                sort: true,
                filterType: "textField",
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                    }
                }),
                customBodyRender: (value, tableMeta) => {
                    return (
                        <Typography className={`${classes.fontSize} ${classes.dateWidth}`}>
                            {value}
                        </Typography>
                    );
                },
            },
        },
        {
            name: 'expiryDate',
            label: intl.formatMessage({ id: 'comp_leave.expiry_date' }),
            options: {
                filter: true,
                sort: true,
                filterType: "textField",
                display: isShiftDuty ? false : true,
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                    }
                }),
                customBodyRender: (value, tableMeta) => {
                    if (page == 'Employee Record') {
                        return (
                            <div className={classes.expiryDate}>
                                <LocalizationProvider 
                                    dateAdapter={AdapterMoment} 
                                    localeText={{ okButtonLabel: 'Update'}}
                                >
                                    <ThemeProvider theme={theme}>
                                        <DatePicker
                                                id='comp-leave-expiry-date'
                                                key={balanceData[tableMeta.rowIndex].id}
                                                name='expiryDate'
                                                format='YYYY-MM-DD'
                                                value={moment(value)}
                                                onAccept={(e) => {
                                                    const convertedDate = moment(e).format('YYYY-MM-DD');
                                                    handleInputChange(
                                                        tableMeta.rowData[0],
                                                        'expiryDate',
                                                        convertedDate
                                                    );
                                                }}
                                                minDate={moment(tableMeta.rowData[1]) || null}
                                                slotProps={{
                                                    textField: {
                                                        variant: 'standard',
                                                        error: false,
                                                    },
                                                    popper: {
                                                        disablePortal: true,
                                                        placement: 'top-start',
                                                        style: { zIndex: 1500 },
                                                    },
                                                    actionBar: {
                                                        actions: ['cancel', 'accept'],
                                                    },
                                                }}
                                                closeOnSelect={false}
                                            />
                                    </ThemeProvider>
                                </LocalizationProvider>

                                {updatedIds && updatedIds.includes(Number(tableMeta.rowData[0])) && (
                                    <div className={classes.updatedText}>
                                        {intl.formatMessage({ id: 'comp_leave.updated' })}
                                    </div>
                                )}
                            </div>
                        )
                    } else {
                        const today = moment();
                        const expiryDate = moment(value);
                        const isExpired = expiryDate.isBefore(today);
                        const oneMonthLater = today.clone().add(1, 'months');
                        const isWithinOneMonthExpiry = expiryDate.isBefore(oneMonthLater);

                        return (
                            <Typography
                                className={`
                                    ${classes.fontSize} 
                                    ${classes.dateWidth}
                                    ${isWithinOneMonthExpiry && !isExpired
                                        ? classes.expiryText
                                        : ''
                                    }
                                `}
                            >
                                {value}
                            </Typography>
                        );
                    }
                },
            },
        },
        {
            name: 'description',
            label: intl.formatMessage({ id: 'comp_leave.justification' }),
            options: {
                filter: true,
                sort: true,
                filterType: "textField",
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                    }
                }),
                customBodyRender: (value, tableMeta, updateValue) => {
                    return (
                        <Typography className={`${classes.descriptionCell} ${classes.fontSize}`}>
                            {value}
                        </Typography>
                    );
                },
            },
        },
        {
            name: 'earn',
            label: intl.formatMessage({ id: 'comp_leave.earn' }),
            options: {
                filter: false,
                sort: true,
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                    }
                }),
            },
        },
        {
            name: 'used',
            label: intl.formatMessage({ id: 'comp_leave.used_comp' }),
            options: {
                filter: false,
                sort: true,
                display: isShiftDuty ? false : true,
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                    }
                }),
            },
        },
        {
            name: 'cashOut',
            label: intl.formatMessage({ id: 'comp_leave.used_cash' }),
            options: {
                filter: false,
                sort: true,
                display: showCashOut ? true : false,
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                    }
                }),
            },
        },
        {
            name: 'remaining',
            label: intl.formatMessage({ id: 'comp_leave.remaining' }),
            options: {
                filter: false,
                sort: true,
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                    }
                }),
            },
        },
        {
            name: 'expired',
            label: intl.formatMessage({ id: 'comp_leave.expired' }),
            options: {
                filter: false,
                sort: true,
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                    }
                }),
                display: false,
            },
        },
    ];

    return columns;
}

export function getColumnsForCompLeaveReqRecord(
    handleOpen,
    handleOpenEdit,
    intl
) {
    const classes = useStyles();
    return [
        {
            name: 'id',
            label: 'ID',
            options: {
                filter: false,
                sort: false,
                display: false,
            },
        },
        {
            name: 'dataSubmitted',
            label: intl.formatMessage({ id: 'table.date_submitted' }),
            options: {
                filter: true,
                sort: true,
                filterType: "textField",
            },
        },
        {
            name: 'period',
            label: intl.formatMessage({ id: 'comp_leave.period' }),
            options: {
                filter: true,
                sort: true,
                filterType: "textField",
            },
        },
        {
            name: 'totalHours',
            label: intl.formatMessage({ id: 'comp_leave.total_hours' }),
            options: {
                filter: false,
                sort: true,
            },
        },
        {
            name: 'status',
            label: intl.formatMessage({ id: 'table.status' }),
            options: {
                filter: true,
                sort: true,
            },
        },
        {
            name: 'progress',
            label: intl.formatMessage({ id: 'comp_leave.progress' }),
            options: {
                filter: false,
                sort: false,
                empty: true,
                customBodyRender: (value, tableMeta) => {
                    return (
                        <TimelineIcon
                            onClick={() => handleOpen(tableMeta.rowData[0])}
                            className={classes.muiDataTableIcon}
                        />
                    );
                },
            },
        },
        {
            name: 'details',
            label: intl.formatMessage({ id: 'table.details' }),
            options: {
                filter: false,
                sort: false,
                empty: true,
                customBodyRender: (value, tableMeta) => {
                    return (
                        <SearchIcon
                            onClick={() => handleOpenEdit(tableMeta.rowData[0])}
                            className={classes.muiDataTableIcon}
                        />
                    );
                },
            },
        },
    ];
}

function textLabels(intl) {
    return {
        body: {
            noMatch: intl.formatMessage({ id: 'table.no_match' }),
            toolTip: intl.formatMessage({ id: 'table.sort' }),
            columnHeaderTooltip: (column) =>
                intl.formatMessage({ id: 'table.sort_for' }) +
                ` ${column.label}`,
        },
        pagination: {
            next: intl.formatMessage({ id: 'table.next' }),
            previous: intl.formatMessage({ id: 'table.previous' }),
            rowsPerPage: intl.formatMessage({ id: 'table.rowsPerPage' }),
            displayRows: intl.formatMessage({ id: 'table.displayRows' }),
        },
        filter: {
            all: intl.formatMessage({ id: 'table.all' }),
            title: intl.formatMessage({ id: 'table.filters' }),
            reset: intl.formatMessage({ id: 'table.reset' }),
        },
        viewColumns: {
            title: intl.formatMessage({ id: 'table.show_columns' }),
            titleAria: intl.formatMessage({
                id: 'table.show_hide_table_columns',
            }),
        },
        selectedRows: {
            text: intl.formatMessage({ id: 'table.rows_selected' }),
            delete: 'Delete',
            deleteAria: 'Delete Selected Rows',
        },
        toolbar: {
            search: intl.formatMessage({ id: 'table.search' }),
            downloadCsv: intl.formatMessage({ id: 'table.downloadCsv' }),
            print: intl.formatMessage({ id: 'table.print' }),
            viewColumns: intl.formatMessage({ id: 'table.viewColumns' }),
            filterTable: intl.formatMessage({ id: 'table.filterTable' }),
        },
    }
}

export function getCompLeaveReqRecordOptions(intl) {
    return {
        filterType: 'checkbox',
        elevation: 1,
        filter: true,
        download: false,
        print: false,
        search: false,
        selectableRows: 'none',
        textLabels: textLabels(intl),
    };
}

export function getBalanceRecordOptions(intl, page, staffDuty) {
    const classes = useStyles();
    const isShiftDuty = localStorage.getItem('staff_duty') === window.global.shiftDuty || staffDuty === window.global.shiftDuty;

    return {
        filterType: 'checkbox',
        elevation: 1,
        filter: true,
        download: false,
        print: false,
        search: false,
        selectableRows: 'none',
        textLabels: textLabels(intl),
        responsive: 'simple',
        setRowProps: (row) => {
            const expiryDate = page == 'Employee Record'
                                ? moment(row[2].props.children[0].props.children.props.children.props.value) 
                                : moment(row[2].props.children);
            const today = moment();
            const isExpired = expiryDate.isBefore(today);
            const oneMonthLater = today.clone().add(1, 'months');
            const isWithinOneMonthExpiry = expiryDate.isBefore(oneMonthLater);

            if (!isShiftDuty) {
                if (isExpired) {
                    return {
                        style: { background: "#E1E1E1" }
                    };
                } else if (isWithinOneMonthExpiry) {
                    return {
                        style: { background: "#FFE6E0" }
                    };
                }
            }

        },
        customTableBodyFooterRender: (opts) => {
            let sumOfEarn = 0;
            let sumOfUsed = 0;
            let sumOfRemaining = 0;
            let sumOfCashOut = 0;
            let sumOfExpired = 0;

            const data = opts?.data;
            sumOfEarn = calculateTotalHours(data, 4);
            sumOfUsed = calculateTotalHours(data, 5);
            sumOfCashOut = calculateTotalHours(data, 6);
            sumOfRemaining = calculateTotalHours(data, 7);
            sumOfExpired = calculateTotalHours(data, 8);

            return (
                page == 'Employee Record' && 
                opts &&
                opts.data.length > 0 && (
                    <TableFooter>
                        <TableRow>
                            {opts.columns.map((col, index) => {
                                if (col.display === 'true') {
                                    if (col.name === 'id') {
                                        return (
                                            <TableCell
                                                key={index}
                                                className={classes.footerCell}
                                            >
                                                {' '}
                                            </TableCell>
                                        );
                                    } else if (col.name === 'dataSubmitted') {
                                        return (
                                            <TableCell
                                                key={index}
                                                className={classes.footerCell}
                                            >
                                                {' '}
                                            </TableCell>
                                        );
                                    } else if (col.name === 'expiryDate') {
                                        return (
                                            <TableCell
                                                key={index}
                                                className={classes.footerCell}
                                            >
                                                {' '}
                                            </TableCell>
                                        );
                                    } else if (col.name === 'description') {
                                        return (
                                            <TableCell
                                                key={index}
                                                className={`${classes.footerCell} ${classes.totalHours}`}
                                            >
                                                {intl.formatMessage({ id: 'comp_leave.total_hours' })}{':'}
                                            </TableCell>
                                        );
                                    } else if (col.name === 'earn') {
                                        return (
                                            <TableCell
                                                key={index}
                                                className={classes.footerCell}
                                            >
                                                {sumOfEarn}
                                            </TableCell>
                                        );
                                    } else if (col.name === 'used') {
                                        return (
                                            <TableCell
                                                key={index}
                                                className={classes.footerCell}
                                            >
                                                {sumOfUsed}
                                            </TableCell>
                                        );
                                    } else if (col.name === 'cashOut') {
                                        return (
                                            <TableCell
                                                key={index}
                                                className={classes.footerCell}
                                            >
                                                {sumOfCashOut}
                                            </TableCell>
                                        );
                                    } else if (col.name === 'remaining') {
                                        return (
                                            <TableCell
                                                key={index}
                                                className={classes.footerCell}
                                            >
                                                {sumOfRemaining}
                                            </TableCell>
                                        );
                                    } else if (col.name === 'expired') {
                                        return (
                                            <TableCell
                                                key={index}
                                                className={classes.footerCell}
                                            >
                                                {sumOfExpired}
                                            </TableCell>
                                        );
                                    }
                                }
                            })}
                        </TableRow>
                    </TableFooter>
                )
            );
        },
    };
}

function calculateTotalHours(data, columnIndex) {
    let totalHours = 0;
    data.forEach((row) => {
        totalHours += Number(row.data[columnIndex]) || 0;
    });
    return totalHours;
}